(function () {
  function ready(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn);
    } else {
      fn();
    }
  }

  function qs(root, sel) {
    return root.querySelector(sel);
  }
  function qsa(root, sel) {
    return Array.from(root.querySelectorAll(sel));
  }

  function getFormById(formId) {
    return formId ? document.getElementById(formId) : null;
  }

  function getVariantsFromPicker(picker) {
    const script = qs(picker, "script[data-aw-variants]");
    if (!script) return null;
    try {
      return JSON.parse(script.textContent || "[]");
    } catch {
      return null;
    }
  }

  const AW_OPTION_HIDDEN_CLASS = "aw-variant-option-hidden";

  function getSelectedOptions(picker) {
    const selected = {};

    const radios = qsa(picker, 'input[type="radio"][name^="option"]');

    [...new Set(radios.map((r) => r.name))].forEach((name) => {
      const checked = qs(
        picker,
        `input[type="radio"][name="${CSS.escape(name)}"]:checked`
      );
      selected[name] = checked ? checked.value : "";
    });

    qsa(picker, 'input[type="hidden"][name^="option"]').forEach((h) => {
      if (!(h.name in selected)) selected[h.name] = h.value || "";
    });

    return selected;
  }

  function getOptionPosition(optionName) {
    const match = /^option(\d+)$/.exec(optionName || "");
    return match ? Number(match[1]) : 0;
  }

  function getOptionKeys(picker) {
    const keys = new Set();

    qsa(picker, "variant-option-value[data-aw-option-name]").forEach((el) => {
      const key = (el.getAttribute("data-aw-option-name") || "").trim();
      if (/^option\d+$/.test(key)) keys.add(key);
    });

    qsa(picker, 'input[name^="option"], select[name^="option"]').forEach((el) => {
      const key = (el.name || "").trim();
      if (/^option\d+$/.test(key)) keys.add(key);
    });

    return Array.from(keys).sort((a, b) => getOptionPosition(a) - getOptionPosition(b));
  }

  function getOptionFieldset(picker, optionName) {
    return (
      qsa(picker, "fieldset.variant-picker__option").find((fieldset) => {
        return !!qs(
          fieldset,
          `variant-option-value[data-aw-option-name="${CSS.escape(optionName)}"]`
        );
      }) || null
    );
  }

  function getOptionSelectorElement(picker, optionName) {
    const fieldset = getOptionFieldset(picker, optionName);
    return fieldset ? qs(fieldset, "[data-option-selector]") || fieldset : null;
  }

  function getOptionTriggerTextElement(picker, optionName) {
    const fieldset = getOptionFieldset(picker, optionName);
    if (!fieldset) return null;

    const ownedId = qs(fieldset, "[data-option-selector][aria-owns]")?.getAttribute(
      "aria-owns"
    );
    return ownedId ? document.getElementById(ownedId) : null;
  }

  function setManagedHidden(el, shouldHide) {
    if (!el) return;

    el.classList.toggle(AW_OPTION_HIDDEN_CLASS, shouldHide);

    if (shouldHide) {
      if (!el.hidden) {
        el.hidden = true;
        el.dataset.awVariantOptionHidden = "1";
      }
      return;
    }

    if (el.dataset.awVariantOptionHidden === "1") {
      el.hidden = false;
      delete el.dataset.awVariantOptionHidden;
    }
  }

  function setManagedAriaHidden(el, shouldHide) {
    if (!el) return;

    if (shouldHide) {
      el.setAttribute("aria-hidden", "true");
      el.dataset.awVariantOptionAriaHidden = "1";
      return;
    }

    if (el.dataset.awVariantOptionAriaHidden === "1") {
      el.removeAttribute("aria-hidden");
      delete el.dataset.awVariantOptionAriaHidden;
    }
  }

  function setManagedDisabled(el, shouldDisable) {
    if (!el || typeof el.disabled !== "boolean") return;

    if (shouldDisable) {
      if (!el.disabled) {
        el.disabled = true;
        el.dataset.awVariantOptionDisabled = "1";
      }
      return;
    }

    if (el.dataset.awVariantOptionDisabled === "1") {
      el.disabled = false;
      delete el.dataset.awVariantOptionDisabled;
    }
  }

  function setOptionTriggerText(picker, optionName, value) {
    const trigger = getOptionTriggerTextElement(picker, optionName);
    if (!trigger) return;

    const nextText = (value || "").trim() || "選択してください";
    if (trigger.textContent !== nextText) {
      trigger.textContent = nextText;
    }
  }

  function syncOptionSelectionUi(picker, optionName, value) {
    const nextValue = (value || "").trim();
    const selector = getOptionSelectorElement(picker, optionName);

    if (selector) {
      qsa(
        selector,
        `input[type="radio"][name="${CSS.escape(optionName)}"]`
      ).forEach((radio) => {
        const isChecked = nextValue !== "" && radio.value === nextValue;
        radio.checked = isChecked;
        radio.defaultChecked = isChecked;

        if (isChecked) {
          radio.setAttribute("checked", "checked");
        } else {
          radio.removeAttribute("checked");
        }
      });

      qsa(selector, 'button[data-option-value][value]').forEach((button) => {
        const isSelected = nextValue !== "" && button.getAttribute("value") === nextValue;

        if (isSelected) {
          button.setAttribute("aria-selected", "true");
        } else {
          button.removeAttribute("aria-selected");
        }
      });
    }

    const hiddenInput = qs(
      picker,
      `input[type="hidden"][name="${CSS.escape(optionName)}"]`
    );
    if (hiddenInput && hiddenInput.value !== nextValue) {
      hiddenInput.value = nextValue;
    }

    setOptionTriggerText(picker, optionName, nextValue);
  }

  function hasVariantForSelection(variants, selection) {
    if (!Array.isArray(variants) || variants.length === 0) return false;

    const entries = Object.keys(selection || {})
      .filter((key) => /^option\d+$/.test(key))
      .map((key) => [key, (selection[key] || "").trim()])
      .filter(([, value]) => value !== "");

    return variants.some((variant) => {
      return entries.every(([key, value]) => String(variant?.[key] || "") === value);
    });
  }

  function hasAvailableVariantForSelection(variants, selection) {
    if (!Array.isArray(variants) || variants.length === 0) return false;

    const entries = Object.keys(selection || {})
      .filter((key) => /^option\d+$/.test(key))
      .map((key) => [key, (selection[key] || "").trim()])
      .filter(([, value]) => value !== "");

    return variants.some((variant) => {
      if (!variant?.available) return false;
      return entries.every(([key, value]) => String(variant?.[key] || "") === value);
    });
  }

  function hasVariantForCandidate(variants, selected, optionName, candidateValue) {
    const optionPosition = getOptionPosition(optionName);
    const selection = {};

    Object.keys(selected || {})
      .filter((key) => getOptionPosition(key) < optionPosition)
      .sort((a, b) => getOptionPosition(a) - getOptionPosition(b))
      .forEach((key) => {
        selection[key] = selected[key] || "";
      });

    selection[optionName] = candidateValue || "";

    return hasVariantForSelection(variants, selection);
  }

  function hasAvailableVariantForCandidate(variants, selected, optionName, candidateValue) {
    const optionPosition = getOptionPosition(optionName);
    const selection = {};

    Object.keys(selected || {})
      .filter((key) => getOptionPosition(key) < optionPosition)
      .sort((a, b) => getOptionPosition(a) - getOptionPosition(b))
      .forEach((key) => {
        selection[key] = selected[key] || "";
      });

    selection[optionName] = candidateValue || "";

    return hasAvailableVariantForSelection(variants, selection);
  }

  function sanitizeInvalidDependentSelections(picker, variants, selected) {
    const optionKeys = getOptionKeys(picker);

    optionKeys.forEach((optionName) => {
      if (getOptionPosition(optionName) <= 1) return;

      const currentValue = (selected[optionName] || "").trim();
      if (!currentValue) return;

      const selectionToValidate = {};

      optionKeys.forEach((key) => {
        if (getOptionPosition(key) > getOptionPosition(optionName)) return;
        selectionToValidate[key] = selected[key] || "";
      });

      if (hasVariantForSelection(variants, selectionToValidate)) return;

      syncOptionSelectionUi(picker, optionName, "");
      selected[optionName] = "";
    });

    return selected;
  }

  function updateOptionValueVisibility(picker, variants, selected) {
    const hideSoldOutVariants = picker.hasAttribute("hide-sold-out-variants");

    getOptionKeys(picker).forEach((optionName) => {
      const selector = getOptionSelectorElement(picker, optionName);
      if (!selector) return;

      qsa(
        selector,
        `input[type="radio"][name="${CSS.escape(optionName)}"]`
      ).forEach((radio) => {
        const exists = hasVariantForCandidate(variants, selected, optionName, radio.value);
        const shouldShow =
          exists &&
          (!hideSoldOutVariants ||
            hasAvailableVariantForCandidate(variants, selected, optionName, radio.value));
        const label =
          qs(selector, `label[data-option-value][for="${CSS.escape(radio.id)}"]`) ||
          qs(picker, `label[data-option-value][for="${CSS.escape(radio.id)}"]`);

        setManagedHidden(radio, !shouldShow);
        setManagedAriaHidden(radio, !shouldShow);
        setManagedDisabled(radio, !shouldShow);

        if (label) {
          setManagedHidden(label, !shouldShow);
          setManagedAriaHidden(label, !shouldShow);
        }
      });

      qsa(selector, 'button[data-option-value][value]').forEach((button) => {
        const candidateValue = button.getAttribute("value") || "";
        const exists = hasVariantForCandidate(
          variants,
          selected,
          optionName,
          candidateValue
        );
        const shouldShow =
          exists &&
          (!hideSoldOutVariants ||
            hasAvailableVariantForCandidate(
              variants,
              selected,
              optionName,
              candidateValue
            ));

        setManagedHidden(button, !shouldShow);
        setManagedAriaHidden(button, !shouldShow);
        setManagedDisabled(button, !shouldShow);
      });
    });
  }

  function allSelected(selected) {
    const keys = Object.keys(selected).filter((k) => /^option\d+$/.test(k));
    return keys.length > 0 && keys.every((k) => (selected[k] || "").trim() !== "");
  }

  function findVariant(variants, selected) {
    const o1 = selected.option1 || "";
    const o2 = selected.option2 || "";
    const o3 = selected.option3 || "";

    return (
      variants.find((v) => {
        if (o1 && v.option1 !== o1) return false;
        if (o2 && v.option2 !== o2) return false;
        if (o3 && v.option3 !== o3) return false;
        return true;
      }) || null
    );
  }

  function findPartialVariantForMedia(variants, selected) {
    const o1 = (selected.option1 || "").trim();
    const o2 = (selected.option2 || "").trim();
    const o3 = (selected.option3 || "").trim();

    return (
      variants.find((v) => {
        if (!v) return false;
        if (o1 && v.option1 !== o1) return false;
        if (o2 && v.option2 !== o2) return false;
        if (o3 && v.option3 !== o3) return false;
        return !!v.featured_media?.id;
      }) || null
    );
  }

  function anyAvailable(variants) {
    return Array.isArray(variants) && variants.some((v) => v && v.available);
  }

  function isRequired(form) {
    const r = qs(form, 'input[name="aw_variant_required"]');
    return r && r.value === "1";
  }

  function setTouched(form) {
    const t = qs(form, 'input[name="aw_variant_touched"]');
    if (t) t.value = "1";
  }

  function setVariantId(form, id) {
    const idInput = qs(form, 'input[name="id"]');
    if (!idInput) return;

    idInput.value = id || "";

    idInput.dispatchEvent(new Event("change", { bubbles: true }));
    idInput.dispatchEvent(new Event("input", { bubbles: true }));
  }

  function syncOptionHiddenInputs(picker, selected) {
    getOptionKeys(picker).forEach((key) => {
      if (!/^option\d+$/.test(key)) return;
      syncOptionSelectionUi(picker, key, selected[key] || "");
    });
  }

  function clearPickerSelection(picker) {
    getOptionKeys(picker).forEach((optionName) => {
      syncOptionSelectionUi(picker, optionName, "");
    });
  }

  function getOptionLabelMap(picker) {
    const map = {};

    qsa(picker, "fieldset.variant-picker__option").forEach((fs) => {
      const vov = qs(fs, "variant-option-value[data-aw-option-name]");
      const key = vov?.getAttribute("data-aw-option-name");
      const legendText = qs(fs, "legend")?.textContent || "";
      const label = legendText.replace(":", "").trim();

      if (key && label) map[key] = label;
    });

    return map;
  }

  function buildMissingMessage(picker, selected) {
    const labels = getOptionLabelMap(picker);

    const keys = Object.keys(selected)
      .filter((k) => /^option\d+$/.test(k))
      .sort((a, b) => Number(a.replace("option", "")) - Number(b.replace("option", "")));

    const missing = keys
      .filter((k) => !(selected[k] || "").trim())
      .map((k) => labels[k] || k);

    if (missing.length === 0) return "";
    if (missing.length === 1) return `${missing[0]}を選択してください`;
    return `${missing.join("・")}を選択してください`;
  }

  function buildSelectAfterTextNoNoun(picker, selected) {
    const msg = picker && selected ? buildMissingMessage(picker, selected) : "";
    const base = msg ? msg.replace(/を選択してください$/, "") : "バリエーション";
    return `${base}を選択後に表示されます`;
  }

  function ensureClickableSubmit(form) {
    const req = form.querySelector('input[name="aw_variant_required"]');
    if (!req || req.value !== "1") return;

    const btns = form.querySelectorAll('button[type="submit"], input[type="submit"]');

    btns.forEach((btn) => {
      if (btn.getAttribute("data-aw-force-disabled") === "1") return;
      btn.removeAttribute("disabled");
      btn.removeAttribute("aria-disabled");
    });
  }

  function blockWithAlertOnly(e, picker, selected) {
    e.preventDefault();
    e.stopPropagation();

    if (e.stopImmediatePropagation) e.stopImmediatePropagation();

    const s = selected || (picker ? getSelectedOptions(picker) : null);
    const text = picker && s ? buildMissingMessage(picker, s) : "バリエーションを選択してください";

    alert(text);

    if (picker) {
      picker.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  function updateOptionDisplays(picker, selected) {
    const displays = qsa(picker, "variant-option-value[data-aw-option-display]");

    displays.forEach((el) => {
      const name = el.getAttribute("data-aw-option-name");
      const val = name ? (selected[name] || "").trim() : "";
      el.textContent = val ? val : "選択してください";
    });

    getOptionKeys(picker).forEach((optionName) => {
      setOptionTriggerText(picker, optionName, selected[optionName] || "");
    });
  }

  function updateVariantParam(variantIdOrEmpty) {
    try {
      const url = new URL(window.location.href);

      if (variantIdOrEmpty) {
        url.searchParams.set("variant", String(variantIdOrEmpty));
      } else {
        url.searchParams.delete("variant");
      }

      const next =
        url.pathname +
        (url.searchParams.toString() ? `?${url.searchParams}` : "") +
        url.hash;

      window.history.replaceState(window.history.state, "", next);
    } catch {}
  }

  function syncGalleryToVariantMedia(formId, variant) {
    if (!formId) return;
    if (!variant || !variant.featured_media || !variant.featured_media.id) return;

    const gallery = document.querySelector(`product-gallery[form="${CSS.escape(formId)}"]`);
    if (!gallery) return;

    const mediaId = String(variant.featured_media.id);

    const mediaItems = qsa(gallery, ".product-gallery__media[data-media-id]");
    if (!mediaItems.length) return;

    const targetIndex = mediaItems.findIndex((item) => {
      return String(item.getAttribute("data-media-id")) === mediaId;
    });

    if (targetIndex < 0) return;

    const thumbnails = qsa(gallery, ".product-gallery__thumbnail-list .product-gallery__thumbnail");
    const targetThumb = thumbnails[targetIndex];

    if (targetThumb) {
      targetThumb.click();
      return;
    }

    const carousel = qs(gallery, "media-carousel");
    const targetMedia = mediaItems[targetIndex];

    if (!carousel || !targetMedia) return;

    targetMedia.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }

  // -----------------------------
  // ボタン / 再入荷 / 売り切れバッジ
  // -----------------------------
  const awDefaultSubmitMetaByForm = Object.create(null);
  const awUiStateByFormId = Object.create(null);
  const awHasUserPickedByFormId = Object.create(null);

  let awUiRafPending = false;

  function getNativeSubmitButtons(form) {
    return qsa(form, 'button[type="submit"], input[type="submit"]');
  }

  function readButtonLabel(btn) {
    if (!btn) return "";

    if (btn.tagName === "INPUT") {
      return (btn.value || "").trim();
    }

    const innerDiv = btn.querySelector(":scope > div");
    return ((innerDiv ? innerDiv.textContent : btn.textContent) || "").trim();
  }

  function writeButtonLabel(btn, label) {
    if (!btn) return;

    if (btn.tagName === "INPUT") {
      if (btn.value !== label) btn.value = label;
      return;
    }

    const innerDiv = btn.querySelector(":scope > div");
    if (innerDiv) {
      if (innerDiv.textContent !== label) innerDiv.textContent = label;
      return;
    }

    btn.textContent = label;
  }

  function isSoldOutLikeLabel(label) {
    return /売り切れ|sold\s*out/i.test(String(label || "").trim());
  }

  function normalizeDefaultSubmitMeta(rawLabel, rawClassName, btn) {
    const isDisabledLike =
      btn.hasAttribute("disabled") ||
      btn.getAttribute("aria-disabled") === "true" ||
      btn.getAttribute("data-aw-force-disabled") === "1";

    const looksSoldOut = isSoldOutLikeLabel(rawLabel) || isDisabledLike;

    const label = looksSoldOut ? "カートに追加" : (rawLabel || "カートに追加");

    let className = rawClassName || "";
    if (looksSoldOut) {
      className = className
        .replace(/\bbutton--subdued\b/g, "")
        .replace(/\s+/g, " ")
        .trim();
    }

    return {
      label,
      className,
    };
  }

  function captureDefaultSubmitMeta(formId) {
    if (!formId) return;
    if (awDefaultSubmitMetaByForm[formId]) return;

    const form = getFormById(formId);
    if (!form) return;

    const btns = getNativeSubmitButtons(form);
    if (!btns.length) return;

    awDefaultSubmitMetaByForm[formId] = btns.map((btn) => {
      const rawLabel = readButtonLabel(btn);
      const rawClassName = btn.className || "";
      const normalized = normalizeDefaultSubmitMeta(rawLabel, rawClassName, btn);

      return {
        label: normalized.label,
        className: normalized.className,
        tagName: btn.tagName,
      };
    });
  }

  function getBuyButtonsWrap(form) {
    return (
      form.querySelector("buy-buttons") ||
      form.querySelector(".buy-buttons") ||
      form.querySelector('button[type="submit"], input[type="submit"]')?.closest(".buy-buttons")
    );
  }

  function buildSoldOutButtonClass() {
    return "button button--xl button--subdued";
  }

  function ensureSoldOutButton(formId) {
    const form = getFormById(formId);
    if (!form) return null;

    const wrap = getBuyButtonsWrap(form);
    if (!wrap) return null;

    let btn = qs(wrap, '[data-aw-soldout-button="1"]');
    if (btn) {
      btn.className = buildSoldOutButtonClass();
      btn.type = "submit";
      btn.setAttribute("is", "custom-button");
      btn.setAttribute("disabled", "");
      btn.innerHTML =
        '<div>売り切れ</div>' +
        '<span class="button__loader">' +
        "  <span></span>" +
        "  <span></span>" +
        "  <span></span>" +
        "</span>";
      return btn;
    }

    btn = document.createElement("button");
    btn.type = "submit";
    btn.className = buildSoldOutButtonClass();
    btn.setAttribute("is", "custom-button");
    btn.setAttribute("data-aw-soldout-button", "1");
    btn.setAttribute("disabled", "");
    btn.hidden = true;

    btn.innerHTML =
      '<div>売り切れ</div>' +
      '<span class="button__loader">' +
      "  <span></span>" +
      "  <span></span>" +
      "  <span></span>" +
      "</span>";

    wrap.appendChild(btn);
    return btn;
  }

  function restoreNativeSubmitButtons(formId) {
    const form = getFormById(formId);
    if (!form) return;

    captureDefaultSubmitMeta(formId);

    const defaults = awDefaultSubmitMetaByForm[formId] || [];
    const btns = getNativeSubmitButtons(form);

    btns.forEach((btn, index) => {
      const meta = defaults[index] || defaults[0] || { label: "カートに追加", className: "" };

      if (meta.className && btn.className !== meta.className) {
        btn.className = meta.className;
      }

      writeButtonLabel(btn, meta.label || "カートに追加");

      btn.hidden = false;
      btn.removeAttribute("data-aw-force-disabled");
      btn.removeAttribute("disabled");
      btn.removeAttribute("aria-disabled");
      btn.style.pointerEvents = "";
      btn.style.opacity = "";
      btn.style.cursor = "";
    });

    const wrap = getBuyButtonsWrap(form);
    if (wrap) wrap.hidden = false;

    const soldOutBtn = ensureSoldOutButton(formId);
    if (soldOutBtn) soldOutBtn.hidden = true;
  }

  function showSoldOutButton(formId) {
    const form = getFormById(formId);
    if (!form) return;

    captureDefaultSubmitMeta(formId);

    const wrap = getBuyButtonsWrap(form);
    if (wrap) wrap.hidden = false;

    getNativeSubmitButtons(form).forEach((btn) => {
      btn.hidden = true;
    });

    const soldOutBtn = ensureSoldOutButton(formId);
    if (soldOutBtn) {
      soldOutBtn.className = buildSoldOutButtonClass(formId);
      soldOutBtn.hidden = false;
    }
  }

  function hideAddToCartArea(formId) {
    const form = getFormById(formId);
    if (!form) return;

    const wrap = getBuyButtonsWrap(form);
    if (wrap) wrap.hidden = true;

    const soldOutBtn = ensureSoldOutButton(formId);
    if (soldOutBtn) soldOutBtn.hidden = true;
  }

  function setRestockHidden(formId, hidden) {
    const form = getFormById(formId);
    if (!form) return;

    const scope = form.closest(".product-info, safe-sticky, .shopify-section") || document;

    const candidates = [
      ...qsa(scope, "#restock-notice-btn"),
      ...qsa(scope, "#_ngc_stock_notice_btn")
        .map((b) => b.closest("#restock-notice-btn") || b.parentElement)
        .filter(Boolean),
    ];

    Array.from(new Set(candidates)).forEach((root) => {
      const block = root.closest(".shopify-block") || root;
      if (block) block.hidden = !!hidden;
    });
  }

  function setSoldOutBadgeVisibility(formId, shouldShow) {
    const badge = document.querySelector(`sold-out-badge[form="${CSS.escape(formId)}"]`);
    if (!badge) return;

    badge.toggleAttribute("hidden", !shouldShow);
  }

  function setFormUiState(formId, nextState) {
    awUiStateByFormId[formId] = nextState;
    scheduleApplyUiState();
  }

  function applyFormUiState(formId) {
    const state = awUiStateByFormId[formId];
    if (!state) return;

    if (state.buttonMode === "normal") {
      restoreNativeSubmitButtons(formId);
    } else if (state.buttonMode === "soldout") {
      showSoldOutButton(formId);
    } else if (state.buttonMode === "hidden") {
      hideAddToCartArea(formId);
    }

    setRestockHidden(formId, !!state.restockHidden);
    setSoldOutBadgeVisibility(formId, !!state.badgeVisible);
  }

  function applyAllUiStates() {
    Object.keys(awUiStateByFormId).forEach((formId) => {
      applyFormUiState(formId);
    });
  }

  function scheduleApplyUiState() {
    if (awUiRafPending) return;

    awUiRafPending = true;

    requestAnimationFrame(() => {
      awUiRafPending = false;
      applyAllUiStates();
    });
  }

  function resetToInitialUnselectedState(picker, form, formId, variants) {
    clearPickerSelection(picker);

    const selected = getSelectedOptions(picker);

    updateOptionValueVisibility(picker, variants, selected);
    updateOptionDisplays(picker, selected);
    syncOptionHiddenInputs(picker, selected);

    setVariantId(form, "");
    updateVariantParam("");

    setVariantSkuText(buildSelectAfterTextNoNoun(picker, selected));
    setVariantPriceFromSelectionState(formId, variants, null);
    setVariantOnSaleBadge(formId, null);

    if (!anyAvailable(variants)) {
      setFormUiState(formId, {
        buttonMode: "soldout",
        restockHidden: false,
        badgeVisible: true,
      });
    } else {
      setFormUiState(formId, {
        buttonMode: "normal",
        restockHidden: true,
        badgeVisible: false,
      });
    }

    ensureClickableSubmit(form);
  }

  function scheduleInitialResetSequence(picker, form, formId, variants) {
    resetToInitialUnselectedState(picker, form, formId, variants);

    requestAnimationFrame(() => {
      if (awHasUserPickedByFormId[formId]) return;
      resetToInitialUnselectedState(picker, form, formId, variants);
    });

    setTimeout(() => {
      if (awHasUserPickedByFormId[formId]) return;
      resetToInitialUnselectedState(picker, form, formId, variants);
    }, 0);

    window.addEventListener(
      "load",
      () => {
        if (awHasUserPickedByFormId[formId]) return;
        resetToInitialUnselectedState(picker, form, formId, variants);
      },
      { once: true }
    );
  }

  // -----------------------------
  // SKU
  // -----------------------------
  let awDesiredSkuText = "";
  let awSkuRafPending = false;

  function applySkuToAllBoxes() {
    const boxes = document.querySelectorAll(".variant_sku_box");
    if (!boxes.length) return;

    const next = awDesiredSkuText || "";
    boxes.forEach((box) => {
      if (box.textContent !== next) box.textContent = next;
    });
  }

  function scheduleApplySku() {
    if (awSkuRafPending) return;
    awSkuRafPending = true;

    requestAnimationFrame(() => {
      awSkuRafPending = false;
      applySkuToAllBoxes();
    });
  }

  function setVariantSkuText(text) {
    awDesiredSkuText = (text || "").trim();
    scheduleApplySku();
  }

  // -----------------------------
  // PRICE：未選択時は「最安値～」
  // -----------------------------
  let awDesiredSaleText = "";
  let awDesiredCompareText = "";
  let awLastFormIdForPrice = "";
  let awPriceRafPending = false;

  function formatYenFromCents(cents) {
    const yen = Math.round(Number(cents || 0) / 100);
    return "¥" + yen.toLocaleString("ja-JP");
  }

  function getPriceRangeInfo(variants) {
    if (!variants || !variants.length) return null;

    const available = variants.filter((v) => v && v.available);
    const pool = available.length ? available : variants;

    let minPrice = null;
    let maxPrice = null;
    let minVariant = null;

    pool.forEach((v) => {
      if (!v) return;
      const p = Number(v.price || 0);

      if (minPrice === null || p < minPrice) {
        minPrice = p;
        minVariant = v;
      }
      if (maxPrice === null || p > maxPrice) {
        maxPrice = p;
      }
    });

    if (minPrice === null || maxPrice === null || !minVariant) return null;

    return { minVariant, hasRange: minPrice !== maxPrice };
  }

  function applyPriceToAll(formId) {
    if (!formId) return;

    const saleEls = document.querySelectorAll(`sale-price[form="${CSS.escape(formId)}"]`);
    const compareEls = document.querySelectorAll(`compare-at-price[form="${CSS.escape(formId)}"]`);

    const saleText = awDesiredSaleText || "";

    saleEls.forEach((el) => {
      const sr = el.querySelector(".sr-only");
      el.textContent = "";
      if (sr) el.appendChild(sr);
      el.appendChild(document.createTextNode(saleText));
    });

    const shouldShowCompare = Boolean(awDesiredCompareText);
    compareEls.forEach((el) => {
      el.toggleAttribute("hidden", !shouldShowCompare);
      if (!shouldShowCompare) return;

      const sr = el.querySelector(".sr-only");
      el.textContent = "";
      if (sr) el.appendChild(sr);
      el.appendChild(document.createTextNode(awDesiredCompareText));
    });

    document.querySelectorAll(".comp_arrow").forEach((arrow) => {
      arrow.toggleAttribute("hidden", !shouldShowCompare);
    });
  }

  function setPriceHintClass(formId, enabled) {
    if (!formId) return;

    const sale = document.querySelector(`sale-price[form="${CSS.escape(formId)}"]`);
    const wrap = sale?.closest(".rating-with-text");
    if (!wrap) return;

    wrap.classList.toggle("aw-price--hint", !!enabled);
  }

  function scheduleApplyPrice(formId) {
    if (awPriceRafPending) return;

    awPriceRafPending = true;

    requestAnimationFrame(() => {
      awPriceRafPending = false;
      applyPriceToAll(formId);
    });
  }

  function setVariantPriceFromSelectionState(formId, variants, selectedVariantOrNull) {
    awLastFormIdForPrice = formId || "";

    if (selectedVariantOrNull) {
      const v = selectedVariantOrNull;
      const cap = Number(v.compare_at_price || 0);
      const p = Number(v.price || 0);

      awDesiredSaleText = formatYenFromCents(p);
      awDesiredCompareText = cap > p ? formatYenFromCents(cap) : "";

      setPriceHintClass(awLastFormIdForPrice, false);
      scheduleApplyPrice(awLastFormIdForPrice);
      return;
    }

    const range = getPriceRangeInfo(variants);
    if (!range) {
      awDesiredSaleText = "";
      awDesiredCompareText = "";
      scheduleApplyPrice(awLastFormIdForPrice);
      return;
    }

    const minV = range.minVariant;
    const suffix = range.hasRange ? "～" : "";

    const cap = Number(minV.compare_at_price || 0);
    const p = Number(minV.price || 0);

    setPriceHintClass(awLastFormIdForPrice, true);

    awDesiredSaleText = `${formatYenFromCents(p)}${suffix}`;
    awDesiredCompareText = cap > p ? formatYenFromCents(cap) : "";

    scheduleApplyPrice(awLastFormIdForPrice);
  }

  // -----------------------------
  // ON SALE BADGE
  // -----------------------------
  let awDesiredOnSaleText = "";
  let awLastFormIdForOnSale = "";
  let awOnSaleRafPending = false;
  
  function getDiscountPercentageText(variant) {
    if (!variant) return "";
    
    const compareAt = Number(variant.compare_at_price || 0);
  const price = Number(variant.price || 0);
  
  if (!compareAt || compareAt <= price) return "";

  const rate = Math.floor(((compareAt - price) / compareAt) * 100);
  if (rate <= 0) return "";
  
  return `${rate}% OFF`;
}

function applyOnSaleBadgeToAll(formId) {
  if (!formId) return;

  const badges = document.querySelectorAll(
    `on-sale-badge[form="${CSS.escape(formId)}"]`
  );
  
  const shouldShow = Boolean(awDesiredOnSaleText);

  badges.forEach((badge) => {
    badge.toggleAttribute("hidden", !shouldShow);

    if (shouldShow && badge.textContent !== awDesiredOnSaleText) {
      badge.textContent = awDesiredOnSaleText;
    }
  });
}

function scheduleApplyOnSaleBadge(formId) {
  if (awOnSaleRafPending) return;
  
  awOnSaleRafPending = true;
  
  requestAnimationFrame(() => {
    awOnSaleRafPending = false;
    applyOnSaleBadgeToAll(formId);
  });
}

function setVariantOnSaleBadge(formId, selectedVariantOrNull) {
  awLastFormIdForOnSale = formId || "";
  awDesiredOnSaleText = getDiscountPercentageText(selectedVariantOrNull);
  scheduleApplyOnSaleBadge(awLastFormIdForOnSale);
}
  function setupObserverOnce() {
    const root = document.body;
    if (!root) return;

    const obs = new MutationObserver(() => {
      scheduleApplySku();
      scheduleApplyPrice(awLastFormIdForPrice);
      scheduleApplyOnSaleBadge(awLastFormIdForOnSale);
      scheduleApplyUiState();
    });

    obs.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
    });
  }

  function releasePending() {
    document.documentElement.classList.remove("aw-variant-pending");
  }

  ready(function () {
    const pickers = qsa(document, "variant-picker");

    if (!pickers.length) {
      releasePending();
      return;
    }

    setupObserverOnce();

    let initializedAnyRequiredPicker = false;

    pickers.forEach((picker) => {
      const formId = picker.getAttribute("form");
      const form = getFormById(formId);

      if (!form) return;
      if (!isRequired(form)) return;

      const variants = getVariantsFromPicker(picker);
      if (!variants || !variants.length) return;

      initializedAnyRequiredPicker = true;

      captureDefaultSubmitMeta(formId);
      awHasUserPickedByFormId[formId] = false;

      scheduleInitialResetSequence(picker, form, formId, variants);

      function onAnyPick(e) {
        if (e && e.isTrusted) {
          awHasUserPickedByFormId[formId] = true;
        }

        const hasAnyStock = anyAvailable(variants);
        let selected = getSelectedOptions(picker);

        if (!awHasUserPickedByFormId[formId] && hasAnyStock) {
          resetToInitialUnselectedState(picker, form, formId, variants);
          return;
        }

        setTouched(form);

        sanitizeInvalidDependentSelections(picker, variants, selected);
        selected = getSelectedOptions(picker);

        updateOptionValueVisibility(picker, variants, selected);
        updateOptionDisplays(picker, selected);
        syncOptionHiddenInputs(picker, selected);

        if (!hasAnyStock) {
          const partialVariant = findPartialVariantForMedia(variants, selected);
          const selectedVariant = allSelected(selected) ? findVariant(variants, selected) : null;
          const displayVariant = selectedVariant || partialVariant || null;

          if (selectedVariant) {
            const vid = String(selectedVariant.id || "");
            setVariantId(form, vid);
            updateVariantParam(vid);
            setVariantSkuText(selectedVariant.sku || "");
            setVariantPriceFromSelectionState(formId, variants, selectedVariant);
            setVariantOnSaleBadge(formId, selectedVariant);
            syncGalleryToVariantMedia(formId, selectedVariant);
          } else {
            setVariantId(form, "");
            updateVariantParam("");
            setVariantSkuText(
              displayVariant?.sku || buildSelectAfterTextNoNoun(picker, selected)
            );
            setVariantPriceFromSelectionState(formId, variants, null);
            setVariantOnSaleBadge(formId, null);
            syncGalleryToVariantMedia(formId, displayVariant);
          }

          setFormUiState(formId, {
            buttonMode: "soldout",
            restockHidden: false,
            badgeVisible: true,
          });

          return;
        }

        if (!allSelected(selected)) {
          const partialVariant = findPartialVariantForMedia(variants, selected);

          setVariantId(form, "");
          updateVariantParam("");
          setVariantSkuText(buildSelectAfterTextNoNoun(picker, selected));
          setVariantPriceFromSelectionState(formId, variants, null);
          setVariantOnSaleBadge(formId, null);
          syncGalleryToVariantMedia(formId, partialVariant);

          setFormUiState(formId, {
            buttonMode: "normal",
            restockHidden: true,
            badgeVisible: false,
          });

          ensureClickableSubmit(form);
          return;
        }

        const variant = findVariant(variants, selected);
        const vid = variant ? String(variant.id) : "";

        setVariantId(form, vid);
        updateVariantParam(vid);

        const isSoldOutSelected = !!variant && variant.available === false;

        if (isSoldOutSelected) {
          setFormUiState(formId, {
            buttonMode: "soldout",
            restockHidden: false,
            badgeVisible: true,
          });
        } else {
          setFormUiState(formId, {
            buttonMode: "normal",
            restockHidden: true,
            badgeVisible: false,
          });
        }

        setTimeout(() => {
          setVariantSkuText(variant?.sku || "");
          setVariantPriceFromSelectionState(formId, variants, variant || null);
          setVariantOnSaleBadge(formId, variant || null);
          syncGalleryToVariantMedia(formId, variant || null);
          ensureClickableSubmit(form);
        }, 0);
      }

      picker.addEventListener("change", onAnyPick, true);

      picker.addEventListener(
        "click",
        (e) => {
          if (e.isTrusted) {
            awHasUserPickedByFormId[formId] = true;
          }

          setTimeout(() => {
            onAnyPick();
          }, 0);
        },
        true
      );

      getNativeSubmitButtons(form).forEach((btn) => {
        btn.addEventListener(
          "click",
          (e) => {
            const state = awUiStateByFormId[formId];

            if (state && state.buttonMode === "soldout") {
              e.preventDefault();
              e.stopPropagation();
              if (e.stopImmediatePropagation) e.stopImmediatePropagation();
              return;
            }

            const idInput = qs(form, 'input[name="id"]');
            if (!idInput || !String(idInput.value || "").trim()) {
              const selected = getSelectedOptions(picker);
              blockWithAlertOnly(e, picker, selected);
            }
          },
          true
        );
      });

      form.addEventListener(
        "submit",
        (e) => {
          const state = awUiStateByFormId[formId];

          if (state && state.buttonMode === "soldout") {
            e.preventDefault();
            e.stopPropagation();
            if (e.stopImmediatePropagation) e.stopImmediatePropagation();
            return;
          }

          const idInput = qs(form, 'input[name="id"]');
          if (!idInput || !String(idInput.value || "").trim()) {
            const selected = getSelectedOptions(picker);
            blockWithAlertOnly(e, picker, selected);
          }
        },
        true
      );
    });

    if (!initializedAnyRequiredPicker) {
      releasePending();
      return;
    }

    releasePending();
  });
})();
